import {
  Box,
  Code,
  HStack,
  ListItem,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import DevSandbox from 'components/DevSandbox';
import EditorSettingsButton, { ShareButton } from 'components/DevSandbox/EditorSettingsButton';
import { MerlinToggleButton } from 'components/DevSandbox/Merlin';
import DiscussionTab from 'components/DevSandbox/PaneTabs/Discussion/DiscussionTab';
import RightSideActions from 'components/DevSandbox/RightSideActions';
import Icon from 'components/Icon';
import Link from 'components/Link';
import ProjectElement from 'components/ProjectElement';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';
import {
  DifficultyMarker,
  StatusIconMapper,
  StatusTextMapper,
} from 'features/practice-problems/PracticeProblems';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Support_Type_Enum, User_Practice_Problem_Status_Enum } from 'types/databaseEnums';
import { PracticeProblem } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';
import { mapExpToProblem } from 'utils/constants';

import ProblemsDrawer from './ProblemsDrawer';

interface IPracticeProblemPlaygroundProps {
  practiceProblem: PracticeProblem;
}

const PracticeProblemPlayground = ({
  practiceProblem: {
    id,
    title,
    description,
    test_cases,
    function_name,
    screen_name,
    difficulty,
    status,
    discussion_count,
  },
}: IPracticeProblemPlaygroundProps) => {
  const { id: userId } = useCurrentUser();
  const { isSmallScreen } = useMediaQueries();

  const codeEditorRef = useRef<any>(null);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  return (
    <>
      <DevSandbox
        ref={codeEditorRef}
        id={`problem-${screen_name}-${userId}`}
        title={title}
        mode="script-practice"
        merlinConfig={{
          entityName: title,
          entityType: 'practice',
          entityDescription: description,
        }}
        navigation={{
          leftSide: <ProblemsDrawer />,
          header: (
            <HStack spacing={2}>
              <MerlinToggleButton />
              <Box data-tour="submit-button" display="inline-flex">
                <ProtectedElement
                  scheme="gradient"
                  leftIcon={<Icon type="SubmitCode" />}
                  onClick={async (e) => {
                    const passedTestsCount = await codeEditorRef.current?.runTests(e);

                    if (passedTestsCount !== test_cases.tests.length) {
                      return;
                    }

                    navigate('submit');
                  }}
                >
                  Submit
                </ProtectedElement>
              </Box>
            </HStack>
          ),
          rightSide: (
            <RightSideActions
              leftActions={
                !isSmallScreen ? (
                  <HStack spacing={2}>
                    <EditorSettingsButton />
                    <ShareButton />
                  </HStack>
                ) : undefined
              }
            />
          ),
        }}
        codeTabs={{
          js: `function ${function_name}(${test_cases.input_names.join(
            ', '
          )}) {\n // Write your code below\n}`,
        }}
        paneTabs={{
          tabs: [
            {
              screenName: 'instructions',
              title: 'Instructions',
              icon: <Icon type="CodeInstructions" size={16} />,
              content: (
                <VStack alignItems="flex-start" p={4} spacing={4}>
                  <VStack alignItems="flex-start" width="100%">
                    <HStack justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                      <HStack>
                        <Text fontSize="xxLarge" fontWeight="bold">
                          {title}
                        </Text>
                        <ProjectElement
                          type={
                            status === User_Practice_Problem_Status_Enum.Completed
                              ? 'experienceCompleted'
                              : 'experience'
                          }
                          experience={mapExpToProblem[difficulty]}
                        />
                      </HStack>
                      <HStack>
                        {StatusIconMapper[status]}
                        <Text fontSize="small">{StatusTextMapper[status]}</Text>
                      </HStack>
                    </HStack>
                    <DifficultyMarker difficulty={difficulty} />
                  </VStack>
                  <ReactMarkdown
                    components={{
                      h1: ({ node, ...props }) => <Text fontSize="xLarge" {...props} />,
                      h2: ({ node, ...props }) => <Text fontSize="xLarge" {...props} />,
                      h3: ({ node, ...props }) => <Text fontSize="large" {...props} />,
                      ul: ({ node, ...props }) => <UnorderedList spacing={2} pl={3} {...props} />,
                      li: ({ node, ...props }) => (
                        <ListItem lineHeight="26px" fontWeight="regular" {...props} />
                      ),
                      a: ({ node, ...props }) => <Link {...props} />,
                      p: ({ node, ...props }) => (
                        <Text lineHeight="26px" {...props} fontWeight="regular" />
                      ),
                      code: ({ node, ...props }) => <Code {...props} variant="subtle" />,
                      blockquote: ({ node, ...props }) => (
                        // @ts-ignore uncompatible types
                        <Box
                          borderLeft="2px solid"
                          borderColor="whiteAlpha.600"
                          pl={3}
                          py={1}
                          ml={2}
                          my={3}
                          fontStyle="italic"
                          {...props}
                        />
                      ),
                    }}
                  >
                    {description}
                  </ReactMarkdown>
                </VStack>
              ),
            },
            {
              screenName: 'discussions',
              title: `${
                discussion_count > 0 ? `Discussions (${discussion_count})` : 'Discussions'
              }`,
              icon: <Icon type="Discussion" size={16} />,
              content: (
                <DiscussionTab
                  entityId={id}
                  type={Support_Type_Enum.Question}
                  refetch={() => {
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetPracticeProblem, screen_name],
                    });
                  }}
                />
              ),
            },
          ],
        }}
        testCases={test_cases}
      />
    </>
  );
};

export default PracticeProblemPlayground;
